import { useEffect, useState, useCallback } from "react";
import { Download, Share, Zap, Bell, Clock } from "lucide-react";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import { useLocale } from "../context/LocaleContext";
import { useSEO } from "../hooks/useSEO";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

const Benefit = ({ icon, title, text }) => (
  <div className="flex items-start gap-3 text-left">
    <span className="shrink-0 w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center">
      {icon}
    </span>
    <div>
      <p className="font-bold text-slate-900 text-sm">{title}</p>
      <p className="text-slate-500 text-xs mt-0.5">{text}</p>
    </div>
  </div>
);

export default function GetAppPage() {
  const { t } = useLocale();
  useSEO({
    title: t(
      "Get the App | Ventura Fresh Laundry — Faster Pickup & Delivery",
      "Descarga la App | Ventura Fresh Laundry — Recogida y Entrega Más Rápida"
    ),
    description: t(
      "Install the Ventura Fresh Laundry app on your phone for one-tap access to pickup & delivery, wash & fold and order tracking. Scan the QR code or tap install.",
      "Instala la app de Ventura Fresh Laundry en tu teléfono para acceso rápido a recogida y entrega, lavado y doblado y seguimiento de órdenes. Escanea el código QR o toca instalar."
    ),
    path: "/get-app",
  });

  const [deferredEvent, setDeferredEvent] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredEvent) return;
    deferredEvent.prompt();
    await deferredEvent.userChoice;
    setDeferredEvent(null);
  }, [deferredEvent]);

  const showIosInstructions = isIos() && !deferredEvent;

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <section className="py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <img src="/android-chrome-192x192.png" alt="Ventura Fresh Laundry" className="w-16 h-16 rounded-2xl shadow-md mb-5" />
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 leading-tight">
              {t("Get the Ventura Fresh Laundry app", "Descarga la app de Ventura Fresh Laundry")}
            </h1>
            <p className="text-slate-500 text-base mb-8">
              {t(
                "One tap from your home screen — schedule pickups, track orders and get notified faster.",
                "Un toque desde tu pantalla de inicio — programa recogidas, rastrea tus órdenes y recibe notificaciones más rápido."
              )}
            </p>

            <div className="space-y-5 mb-8">
              <Benefit
                icon={<Zap className="w-5 h-5" />}
                title={t("Faster access", "Acceso más rápido")}
                text={t("No app store, no download size — it opens instantly.", "Sin tienda de apps, sin tamaño de descarga — abre al instante.")}
              />
              <Benefit
                icon={<Bell className="w-5 h-5" />}
                title={t("Order updates", "Actualizaciones de tu orden")}
                text={t("Get notified the moment your laundry is ready.", "Recibe una notificación en cuanto tu ropa esté lista.")}
              />
              <Benefit
                icon={<Clock className="w-5 h-5" />}
                title={t("Schedule in seconds", "Programa en segundos")}
                text={t("Your info is saved — booking a pickup takes seconds.", "Tu información queda guardada — programar una recogida toma segundos.")}
              />
            </div>

            {installed ? (
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-bold rounded-full px-5 py-3">
                {t("App installed — you're all set!", "¡App instalada, todo listo!")}
              </div>
            ) : deferredEvent ? (
              <button
                onClick={handleInstall}
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-full px-7 py-3.5 shadow-lg shadow-sky-200 transition-colors"
                data-testid="get-app-install-btn"
              >
                <Download className="w-4 h-4" />
                {t("Install App", "Instalar App")}
              </button>
            ) : showIosInstructions ? (
              <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-600 text-sm font-semibold rounded-full px-5 py-3">
                <Share className="w-4 h-4" />
                {t("Tap Share → Add to Home Screen", "Toca Compartir → Agregar a inicio")}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">
                {t("Open this page from your phone's browser to install.", "Abre esta página desde el navegador de tu teléfono para instalar.")}
              </p>
            )}
          </div>

          <div className="flex flex-col items-center justify-center">
            <div className="bg-white border border-slate-200 rounded-3xl shadow-xl p-6 sm:p-8">
              <img
                src={`${API_URL}/api/pwa/install-qr.png`}
                alt={t("Scan to install the app", "Escanea para instalar la app")}
                className="w-48 h-48 sm:w-56 sm:h-56"
                data-testid="get-app-qr"
              />
            </div>
            <p className="text-slate-400 text-xs mt-4 text-center max-w-[220px]">
              {t("Scan with your phone's camera to open this page and install.", "Escanea con la cámara de tu teléfono para abrir esta página e instalar.")}
            </p>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
